import Image from 'next/image';

import { atId, cn } from '../../utils';

import Layout from './layout';
import Form from './form';
import Wrapper from './wrapper';

type TextElementProps = {
  text?: string;
  componentProps?: React.ComponentProps<'span'>;
};

type AgeGateProps = {
  logoProps?: {
    url?: string;
    componentProps?: React.ComponentProps<typeof Image>;
  };
  headerTextProps?: TextElementProps;
  subTitleTextProps?: TextElementProps;
  bottomTextProps?: TextElementProps;
  footerLogo?: React.ReactNode;
  config?: Pick<
    React.ComponentProps<typeof Wrapper>,
    'localStorageKey' | 'isPublicPage' | 'alwaysVisible'
  > & {
    // Default age limit is 18
    ageLimit?: number;
  };
  layoutProps?: Omit<React.ComponentProps<typeof Layout>, 'children'>;
  formProps?: Omit<
    React.ComponentProps<typeof Form>,
    'onAgeGatePassed' | 'onAgeGateBlocked' | 'ageLimit'
  >;
  contentContainerClassName?: string;
} & Pick<React.ComponentProps<typeof Wrapper>, 'ageLimitProps'> &
  Pick<React.ComponentProps<typeof Form>, 'onAgeGatePassed' | 'onAgeGateBlocked'>;

export default function AgeGate(props: React.PropsWithChildren<AgeGateProps>) {
  return (
    <Wrapper
      {...(props.config || {})}
      ageLimitProps={props.ageLimitProps}
      allowedContent={props.children}
      ageLimit={props.config?.ageLimit}
    >
      <Layout {...(props.layoutProps || {})}>
        <div
          className={cn(
            'flex h-full flex-col items-center justify-between',
            props.contentContainerClassName,
          )}
        >
          <div className="flex flex-col items-center justify-center p-10 text-center">
            {props.logoProps?.url && (
              <Image
                fill
                data-test-id={atId('ageGateBrandLogo', 'Img')}
                {...(props.logoProps.componentProps ?? { alt: '' })}
                className={cn('!static !h-20 !w-auto', props.logoProps.componentProps?.className)}
                src={props.logoProps.url}
              />
            )}

            <span
              data-test-id={atId('ageGateTitle', 'Val')}
              {...(props.headerTextProps?.componentProps ?? {})}
              className={cn(
                'mb-3 mt-6 font-headline-m text-headline-m',
                props.headerTextProps?.componentProps?.className,
              )}
            >
              {props.headerTextProps?.text || 'Enter your date of birth'}
            </span>

            <span
              data-test-id={atId('ageGateSubtitle', 'Val')}
              {...(props.subTitleTextProps?.componentProps ?? {})}
              className={cn(
                'font-body-m text-body-m',
                props.subTitleTextProps?.componentProps?.className,
              )}
            >
              {props.subTitleTextProps?.text ||
                'To visit this website you must be older than 18 years.'}
            </span>

            <Form
              {...(props.formProps || {})}
              onAgeGateBlocked={props.onAgeGateBlocked}
              onAgeGatePassed={props.onAgeGatePassed}
              ageLimit={props.config?.ageLimit}
            />

            <span
              data-test-id={atId('ageGateLegalWarning', 'Val')}
              {...(props.bottomTextProps?.componentProps ?? {})}
              className={cn(
                'mt-6 font-body-s [&]:text-body-s',
                props.bottomTextProps?.componentProps?.className,
              )}
            >
              {props.bottomTextProps?.text ||
                'Please do not share or forward this website or its contents to anyone under 18.'}
            </span>
          </div>
          {props.footerLogo ? (
            <div className="mb-10 flex grow items-end">{props.footerLogo}</div>
          ) : null}
        </div>
      </Layout>
    </Wrapper>
  );
}
